import { memo, useMemo } from 'react';
import { BulkActionBar } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Trash2, UserCheck, Wand2 } from 'lucide-react';

/**
 * Bar above the review table while rows are selected: Assign access, Apply access from file
 * and Remove from list. There is no invite action here: invitations are chosen in step 4.
 *
 * @param {object} props
 * @param {number} props.selectedCount
 * @param {number} props.pageCount Rows on the current page.
 * @param {number} props.totalCount Rows that pass the filters, across all pages.
 * @param {() => void} props.onClear
 * @param {() => void} props.onSelectAll
 * @param {() => void} props.onAssign
 * @param {() => void} props.onApplyFromFile
 * @param {() => void} props.onRemove
 */
function ReviewBulkBar({ selectedCount, pageCount, totalCount, onClear, onSelectAll, onAssign, onApplyFromFile, onRemove }) {
  const { t } = useTranslation('people-access');
  // One person is edited on their own row: the bulk actions need two or more.
  const single = selectedCount === 1;
  const actions = useMemo(
    () => single ? [] : [
      { id: 'assign', label: t('import.review.bulk.assign'), icon: UserCheck, variant: 'primary', onClick: onAssign },
      { id: 'apply', label: t('import.review.bulk.applyFromFile'), icon: Wand2, onClick: onApplyFromFile },
      { id: 'remove', label: t('import.review.bulk.remove'), icon: Trash2, variant: 'danger', onClick: onRemove },
    ],
    [single, t, onAssign, onApplyFromFile, onRemove],
  );

  const bar = (
    <BulkActionBar
      selectedLabel={t('import.review.bulk.selected', { count: selectedCount })}
      actions={actions}
      onClear={onClear}
      clearLabel={t('import.review.bulk.clear')}
      selectedCount={selectedCount}
      pageCount={pageCount}
      totalCount={totalCount}
      onSelectAll={onSelectAll}
      selectAllLabel={t('import.review.bulk.selectAll', { count: totalCount })}
      actionsAlign="start"
    />
  );
  if (!single) return bar;
  return (
    <div className="flex flex-col gap-2">
      {bar}
      <p className="text-xs text-content-secondary">{t('import.review.bulk.singleHint')}</p>
    </div>
  );
}

export default memo(ReviewBulkBar);
